import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BackButton from "../../../shared/components/Backbtn";
import {
  TextField,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { PhotoCamera, VideoCameraBack } from "@mui/icons-material";
import { db, firebaseAuth } from "../../../context/Firebase"; // Keep db for user fetching temporarily
import { doc, getDoc } from "firebase/firestore"; // Keep for user fetching temporarily
import { useAuthState } from "react-firebase-hooks/auth";
import FirebasePostRepository from "../api/FirebasePostRepository";
import CreatePostUseCase from "../use-cases/CreatePostUseCase";

const AddPost = () => {
  const [postText, setPostText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isVideo, setIsVideo] = useState(false);
  const [user, setUser] = useState(null);
  const [detectedHashtags, setDetectedHashtags] = useState([]);
  const navigate = useNavigate();
  const [authUser] = useAuthState(firebaseAuth);

  const postRepository = new FirebasePostRepository();
  const createPostUseCase = new CreatePostUseCase(postRepository);

  // Extract hashtags from text
  useEffect(() => {
    const hashtagRegex = /#(\w+)/g;
    const matches = postText.match(hashtagRegex);
    setDetectedHashtags(matches || []);
  }, [postText]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (authUser) {
        const userDocRef = doc(db, "users", authUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setUser(userDoc.data());
        } else {
          console.error("User not found");
        }
      }
    };
    fetchUserDetails();
  }, [authUser]); // db is a stable Firebase reference, not a reactive dependency

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedFiles(files);
      setIsVideo(false);
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFiles([file]);
      setIsVideo(true);
    }
  };

  const handleSubmit = async () => {
    const isPostTextPresent = postText.trim() !== '';
    const isImageValid = selectedFiles.length > 0 && selectedFiles.some(file => file.type.startsWith('image/'));
    const isVideoValid = selectedFiles.length > 0 && selectedFiles[0].type.startsWith('video/');

    const hasValidContent = isPostTextPresent || isImageValid || isVideoValid;

    if (!hasValidContent) {
      alert("Please add a post message, image, or video.");
      return;
    }

    if (!user) {
      alert("User details missing.");
      return;
    }

    setIsLoading(true);

    try {
      const postData = {
        userId: authUser.uid,
        userName: user?.name || "Anonymous",
        userProfilePic: user?.picture || "",
        content: postText.trim(),
        imageFile: selectedFiles.length > 0 ? selectedFiles[0] : null, // Assuming single image/video for now
        isVideo: isVideo,
      };

      await createPostUseCase.execute(postData);

      alert("Post created successfully!");
      navigate("/post");
    } catch (err) {
      console.error("Post error:", err);
      alert("Post failed. Try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center min-h-screen p-5 bg-gray-50">
      <div className="w-full flex justify-end">
        <BackButton tooltip="Back" />
      </div>

      <TextField
        multiline
        rows={4}
        placeholder="What's on your mind? Use #hashtags to categorize your post"
        value={postText}
        onChange={(e) => setPostText(e.target.value)}
        fullWidth
        variant="outlined"
        margin="normal"
        className="w-full max-w-2xl bg-white rounded-lg shadow-sm"
      />

      {/* Display detected hashtags */}
      {detectedHashtags.length > 0 && (
        <div className="w-full max-w-2xl p-3 rounded-lg" style={{ backgroundColor: 'var(--app-active-bg)' }}>
          <p className="text-sm text-gray-600 mb-2">Detected hashtags:</p>
          <div className="flex flex-wrap gap-2">
            {detectedHashtags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 text-white rounded-full text-sm"
                style={{ backgroundColor: 'var(--app-accent)' }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap w-full max-w-2xl gap-3 my-5">
        {selectedFiles.map((file, index) => (
          <div key={index}>
            {isVideo ? (
              <video
                src={URL.createObjectURL(file)}
                controls
                className="object-cover w-24 h-24 rounded-lg shadow-md"
              />
            ) : (
              <img
                src={URL.createObjectURL(file)}
                alt={`Preview ${index + 1}`}
                className="object-cover w-24 h-24 rounded-lg shadow-md"
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-4 mb-5">
        <input
          accept="image/png,image/jpeg,image/jpg"
          id="image-upload"
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <label htmlFor="image-upload">
          <IconButton component="span" sx={{ color: 'white', bgcolor: 'var(--app-accent)', '&:hover': { bgcolor: 'var(--app-accent-hover)' } }}>
            <PhotoCamera />
          </IconButton>
        </label>

        <input
          accept="video/mp4,video/webm"
          id="video-upload"
          type="file"
          style={{ display: "none" }}
          onChange={handleVideoChange}
        />
        <label htmlFor="video-upload">
          <IconButton component="span" sx={{ color: 'white', bgcolor: 'var(--app-accent)', '&:hover': { bgcolor: 'var(--app-accent-hover)' } }}>
            <VideoCameraBack />
          </IconButton>
        </label>
      </div>

      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={isLoading}
        className="w-full max-w-2xl px-4 py-2 font-bold text-white rounded-lg shadow-md"
      >
        {isLoading ? <CircularProgress size={24} className="text-white" /> : "Post"}
      </Button>
    </div>
  );
};

export default AddPost;
